import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Navigate } from '@ngxs/router-plugin';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { catchError, tap } from 'rxjs/operators';
import { TOASTER_MSG, MAIN_MENU, AUTH_ROUTES } from 'src/app/app-constants';
import { UnAuthenticatedUserService } from 'src/app/shared/challenge-changePassword-service/un-authenticated-user.service';

import { PinLoginModel } from '../models/pin-login.model';

import { AuthAction } from './auth.action';
import { AuthService } from './auth.service';
import { AuthStateModel } from './auth.state.model';

@State<AuthStateModel>({
    name: 'auth',
    defaults: new AuthStateModel(),
})
@Injectable()
export class AuthState {
    constructor(
        private authService: AuthService,
        private snackBar: MatSnackBar,
        private UnAuthenticatedUserService: UnAuthenticatedUserService
    ) {}

    @Selector()
    static token(state: AuthStateModel) {
        return state.access_token;
    }

    @Selector()
    static pinToken(state: AuthStateModel) {
        return state.pinToken;
    }

    @Selector()
    static pinLogin(state: AuthStateModel): PinLoginModel {
        return state.pinLogin;
    }

    @Selector()
    static signUpErrors(state: AuthStateModel) {
        return state.signUpForm.errors;
    }

    @Selector()
    static pinLoginErrors(state: AuthStateModel) {
        return state.pinCodeForm.errors;
    }

    @Selector()
    static forgotErrors(state: AuthStateModel) {
        return state.forgotForm.errors;
    }

    @Selector()
    static forgotPinErrors(state: AuthStateModel) {
        return state.forgotPinForm.errors;
    }

    @Selector()
    static resetErrors(state: AuthStateModel) {
        return state.resetForm.errors;
    }

    @Selector()
    static orgTypes(state: AuthStateModel) {
        return state.orgTypes;
    }

    @Selector()
    static feedbackErrors(state: AuthStateModel) {
        return state.feedBackForm.errors;
    }

    @Selector()
    static loginFormError(state: AuthStateModel) {
        return state.loginError;
    }

    @Action(AuthAction.MainLogin)
    mainloginForm(ctx: StateContext<AuthStateModel>, action: AuthAction.MainLogin) {
        return this.authService
            .loginWithCognito(action.payload)
            .then(async (resp) => {
                if (resp?.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                    this.UnAuthenticatedUserService.user = resp;
                    ctx.dispatch(new Navigate(['challenge-change-password']));
                } else {
                    const session = await fetchAuthSession();
                    ctx.patchState({
                        access_token: session.tokens?.accessToken?.toString(),
                    });
                    ctx.dispatch(new Navigate(['main']));
                    fetchUserAttributes().then((user) => {
                        const currentUser = user;
                        if (currentUser) {
                            window.localStorage.setItem('userData', JSON.stringify(currentUser));
                        }
                    });
                }
            })
            .catch((err) => {
                ctx.patchState({
                    loginError: this.checkErrors(err),
                });
            });
    }
    // TODO Commenting this function for now not needed
    // async getAuthSession() {
    //     const session: AuthSession = await fetchAuthSession();
    //     return session.tokens?.accessToken?.toString();
    // }

    @Action(AuthAction.PinLogin)
    pinLogin(ctx: StateContext<AuthStateModel>) {
        const { pinCodeForm } = ctx.getState();
        return this.authService.pinLogin(pinCodeForm.model).pipe(
            tap((resp) => {
                this.snackBar.open(TOASTER_MSG.auth.login.display);
                const pinToken = resp.accesstoken;
                const pinLogin = new PinLoginModel(resp);
                ctx.patchState({
                    pinToken,
                    pinLogin,
                });

                if (ctx.getState().pinLogin.isAdmin || ctx.getState().pinLogin.isManager) {
                    ctx.dispatch(new Navigate(['main']));
                } else if (ctx.getState().pinLogin.isOperator) {
                    ctx.dispatch(new Navigate(MAIN_MENU.pos.link));
                }
            }),
            catchError((err) => {
                const state = ctx.getState();
                ctx.setState({
                    ...state,
                    pinCodeForm: {
                        ...state.pinCodeForm,
                        errors: { pin: err },
                    },
                });
                throw err;
            })
        );
    }

    @Action(AuthAction.StaffMe)
    staffMe(ctx: StateContext<AuthStateModel>) {
        return this.authService.staffMe().pipe(
            tap((resp) => {
                ctx.patchState({
                    pinLogin: resp,
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    pinToken: null,
                });
                throw err;
            })
        );
    }

    @Action(AuthAction.MainLogout)
    mainLogout(ctx: StateContext<AuthStateModel>) {
        return this.authService.logoutWithCognito().then(() => {
            ctx.dispatch(new Navigate(['auth/login']));
            ctx.setState(new AuthStateModel());
        });
    }

    @Action(AuthAction.PinLogout)
    pinLogout(ctx: StateContext<AuthStateModel>) {
        ctx.dispatch(new Navigate(['auth/login']));
        ctx.setState(new AuthStateModel());
    }

    @Action(AuthAction.AddOrg)
    addOrg(ctx: StateContext<AuthStateModel>, action: AuthAction.AddOrg) {
        return this.authService.createOrg(action.payload).pipe(
            tap((resp) => {
                // here will do further work
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    @Action(AuthAction.SignUp)
    signUp(ctx: StateContext<AuthStateModel>, action: AuthAction.SignUp) {
        const { signUpForm } = ctx.getState();
        return this.authService
            .signUpWithCognito(action.payload)
            .then((resp) => {
                ctx.patchState({
                    user_pool_id: resp?.userId,
                });
            })
            .catch((err) => {
                const state = ctx.getState();
                ctx.setState({
                    ...state,
                    signUpForm: {
                        ...state.signUpForm,
                        errors: this.checkErrors(err),
                    },
                });
                throw err;
            });
    }

    @Action(AuthAction.ForgotPassword)
    forgotPassword(ctx: StateContext<AuthStateModel>, action: AuthAction.ForgotPassword) {
        const email = action.payload?.email;
        if (!email) {
            throw new Error('Email is required for forgot password');
        }
        return this.authService.forgotWithCognito({ email }).catch((err) => {
            const state = ctx.getState();
            ctx.setState({
                ...state,
                forgotForm: {
                    ...state.forgotForm,
                    errors: this.checkErrors(err),
                },
            });
            throw err;
        });
    }

    @Action(AuthAction.ForgotPin)
    forgotPin(ctx: StateContext<AuthStateModel>) {
        const { forgotPinForm } = ctx.getState();
        return this.authService.forgotPin(forgotPinForm.model).pipe(
            tap((resp) => {
                this.snackBar.open(TOASTER_MSG.auth.forgotPin.display);
                ctx.dispatch(new Navigate([AUTH_ROUTES.pin.link]));
            }),
            catchError((err) => {
                const state = ctx.getState();
                ctx.setState({
                    ...state,
                    forgotPinForm: {
                        ...state.forgotPinForm,
                        errors: err,
                    },
                });
                throw err;
            })
        );
    }

    @Action(AuthAction.ResetPassword)
    resetPassword(ctx: StateContext<AuthStateModel>) {
        const { resetForm } = ctx.getState();
        return this.authService.reset(resetForm.model).pipe(
            tap((resp) => {
                this.snackBar.open(TOASTER_MSG.auth.resetPassword.display);
                ctx.dispatch(new Navigate(['auth/login']));
            }),
            catchError((err) => {
                const state = ctx.getState();
                ctx.setState({
                    ...state,
                    resetForm: {
                        ...state.resetForm,
                        errors: err,
                    },
                });
                throw err;
            })
        );
    }

    @Action(AuthAction.OrgTypes)
    OrgTypes(ctx: StateContext<AuthStateModel>) {
        return this.authService.orgTypes().pipe(
            tap((resp) => {
                ctx.patchState({
                    orgTypes: resp,
                });
            })
        );
    }

    @Action(AuthAction.FeedBack)
    FeedBack(ctx: StateContext<AuthStateModel>, action: AuthAction.FeedBack) {
        const { feedBackForm } = ctx.getState();
        return this.authService.feedBack(action.payload).pipe(
            tap((resp) => {
                ctx.patchState({
                    feedBackForm: {
                        ...feedBackForm,
                        model: {
                            ...feedBackForm.model,
                            message: '',
                            image: '',
                        },
                    },
                });
            }),

            catchError((err) => {
                const state = ctx.getState();
                ctx.setState({
                    ...state,
                    feedBackForm: {
                        ...state.feedBackForm,
                        errors: err,
                    },
                });
                throw err;
            })
        );
    }

    private checkErrors(errorResp) {
        //TODO:have to move these error messages to Const file.
        if (errorResp.message === 'Username cannot be empty') {
            return { email: 'Email cannot be empty' };
        } else if (errorResp?.message === 'Incorrect username or password.') {
            return { password: 'Incorrect email or password' };
        } else if (errorResp.message === 'CUSTOM_AUTH is not enabled for the client.') {
            return { password: 'Password cannot be empty' };
        } else if (errorResp.message?.includes('Member must have length greater than or equal to 6')) {
            return {
                password: 'Password must have length greater than or equal to 6',
            };
        } else if (errorResp.message === 'Password cannot be empty') {
            return { password: errorResp.message };
        } else if (errorResp.message === 'Username should be an email.') {
            return { email: errorResp.message };
        } else if (errorResp.message === 'An account with the given email already exists.') {
            return { email: errorResp.message };
        } else if (errorResp.message === 'User is not confirmed.') {
            return {
                password: 'User is not confirmed. Kindly check your email for verification',
            };
        } else if (errorResp.message === 'Invalid phone number format.') {
            return { contact_number: `Invalid contact number format.` };
        }
    }
}
